import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Burgundy variants
        burgundy: "bg-gradient-to-r from-[hsl(345,60%,35%)] to-[hsl(345,55%,45%)] text-white font-semibold shadow-burgundy hover:shadow-burgundy-lg hover:scale-[1.02] active:scale-[0.98]",
        burgundyOutline: "border-2 border-[hsl(345,60%,35%)] text-[hsl(345,60%,35%)] bg-transparent hover:bg-[hsl(345,60%,35%)] hover:text-white shadow-sm",
        burgundyGhost: "text-[hsl(345,60%,35%)] hover:bg-[hsl(345,60%,35%)/0.1] hover:text-[hsl(345,55%,45%)]",
        // Premium variants
        premium: "bg-gradient-to-r from-[hsl(345,65%,25%)] via-[hsl(345,60%,35%)] to-[hsl(345,65%,25%)] text-white font-semibold shadow-burgundy-lg hover:shadow-[0_0_40px_hsl(345,60%,35%,0.3)] animate-glow-pulse",
        // WhatsApp variant
        whatsapp: "bg-[#25D366] text-white hover:bg-[#20BD5A] shadow-md hover:shadow-lg",
        // Call variant
        call: "bg-[#007bff] text-white hover:bg-[#0056b3] shadow-md hover:shadow-lg",
        // Light elegant
        elegant: "bg-white text-[hsl(0,0%,15%)] border border-[hsl(345,20%,85%)] hover:border-[hsl(345,60%,35%)] hover:text-[hsl(345,60%,35%)] shadow-soft",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-4 text-xs",
        lg: "h-12 rounded-md px-8 text-base",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
